import { bgBlur } from "./../../../utils/cssStyles";
import {
  Stack,
  Drawer,
  Button,
  Divider,
  useTheme,
  Checkbox,
  Typography,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup
} from "@material-ui/core";
import DateRangeFilter, {
  EMPTY_DATE_RANGE_FILTER,
  type DatePreset
} from "components/_dashboard/common/DateRangeFilter";
import { Close } from "@material-ui/icons";
import { useContext } from "react";
import {
  ListingsMatchContext,
  hiddenBooleanPropertiesDetailsKeys
} from "contexts/ListingsMatchContext";
import type { SearchState } from "contexts/ListingsMatchContext";
import FloorRangePicker from "./FloorRangePicker";
import { useTranslation } from "react-i18next";
import PriceRangePicker from "./PriceRangePicker";
import {
  Room,
  Floor,
  ParkingSpot,
  parkingSpots,
  GenericPropertyDetails,
  adTypes as adTypesValues,
  listingListedFor as listingListedForValues,
  ListingListedFor,
  rooms,
  ListingOrigin
} from "./../../../@types/sharedSchema";
import { SimpleMultiSelect } from "components/__haim/Common/SimpleMultiSelect";
import { PropertyTypeSelect } from "components/__haim/Common/PropertyTypeSelect";
import { ListingBooleanPropertiesDetailsBag } from "./ListingPropertiesAndAdditionalDetails";
import { BooleanRadioPicker } from "./BooleanRadioPicker";
import NumberRangePicker from "./NumberRangePicker";
import SizeRangePicker from "./SizeRangePicker";
import { AgencyNamesPicker } from "./AgencyNamesPicker";
import { SellerNamesPicker } from "./SellerNamesPicker";
import { noteHealthRender } from "./listingsMatchHealth";

// export const AudienceSizes = [NanoInfluencer, MicroInfluencer, MidInfluencer, MacroInfluencer, MegaInfluencer];
// export type AudienceSize = typeof AudienceSizes[number];

// export const audienceSizeMapToRange = {
//   [NanoInfluencer]: [0, 10000],
//   [MicroInfluencer]: [10000, 100000],
//   [MidInfluencer]: [100000, 500000],
//   [MacroInfluencer]: [500000, 1000000],
//   [MegaInfluencer]: [1000000, 3000000000]
// };

export type FrozenFilters = {
  listingListedFor?: boolean;
  adTypes?: boolean;
  listingOrigins?: boolean;
  isAssetExclusive?: boolean;
  agencyNames?: boolean;
  sellerNames?: boolean;
  priceRangeMax?: number;
};

type ContextOverride = {
  draftSearchState: SearchState;
  availablePropertyTypes: string[];
  setDraftSearchState: (s: Partial<SearchState>) => void;
  applyDraftSearchState: () => void;
};

export type ListingsFiltersDrawerProps = {
  isDrawerOpen: boolean;
  onCloseRequest: () => void;
  frozenFilters?: FrozenFilters;
  contextOverride?: ContextOverride;
};

export function ListingsFiltersDrawer({
  isDrawerOpen,
  onCloseRequest,
  frozenFilters,
  contextOverride
}: ListingsFiltersDrawerProps) {
  noteHealthRender(isDrawerOpen ? "drawerOpen" : "drawerClosed");
  const theme = useTheme();
  const { t: translate } = useTranslation();

  const listingsMatchCtx = useContext(ListingsMatchContext);
  const ctx = contextOverride ?? listingsMatchCtx;
  if (!ctx) throw new Error("ListingsFiltersDrawer requires either contextOverride or ListingsMatchContextProvider");
  const { draftSearchState, availablePropertyTypes, setDraftSearchState, applyDraftSearchState } = ctx;

  const onSearch = () => {
    applyDraftSearchState();
    onCloseRequest();
  };

  const handleChangePropertyDetails = (name: keyof GenericPropertyDetails) => (): void => {
    console.log(
      "handleChangePropertyDetails",
      name,
      draftSearchState.booleanPropertiesDetails[name]
    );
    setDraftSearchState({
      ...draftSearchState,
      booleanPropertiesDetails: {
        ...draftSearchState.booleanPropertiesDetails,
        [name]: !(draftSearchState.booleanPropertiesDetails[name] ?? false)
      }
    });
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={onCloseRequest}
      PaperProps={{
        sx: {
          ...bgBlur({
            color: theme.palette.background.default,
            opacity: 0.95
          }),
          width: { xs: "100%", sm: 350, md: 450 }
        }
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={3}
        sx={{ py: 2, pr: 2.5, pl: 2.5 }}
      >
        <Stack direction="row" justifyContent={"space-between"} width={"100%"} spacing={1}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            {translate("listingsMatch.filters.title")}
          </Typography>
          <Button variant="text" size="small" onClick={onCloseRequest}>
            <Close />
          </Button>
        </Stack>

        <Stack gap={1} width={"100%"}>
          <Typography variant={"subtitle2"}>
            {translate("listingsMatch.filters.propertyTypesTitle")}
          </Typography>
          {/* Grouped (דירות / צמודי קרקע / מסחרי / אחר) drop-in for
              SimpleMultiSelect — same string[] value shape, so the Algolia
              filter builder still receives the raw Hebrew facet values. */}
          <PropertyTypeSelect
            label={translate("listingsMatch.filters.propertyTypesTitle")}
            emptyLabel={translate("listingsMatch.filters.allOptions")}
            value={draftSearchState.propertyTypes}
            options={availablePropertyTypes}
            onChange={(value) => setDraftSearchState({ propertyTypes: value })}
          />
        </Stack>

        {!frozenFilters?.agencyNames && (
          <Stack gap={1} width={"100%"}>
            <Typography variant={"subtitle2"}>
              {translate("listingsMatch.filters.agencyNamesTitle")}
            </Typography>
            <AgencyNamesPicker
              {...(contextOverride ? { draftSearchState, setDraftSearchState } : {})}
            />
          </Stack>
        )}

        {!frozenFilters?.sellerNames && (
          <Stack gap={1} width={"100%"}>
            <Typography variant={"subtitle2"}>
              {translate("listingsMatch.filters.sellerNamesTitle")}
            </Typography>
            <SellerNamesPicker
              {...(contextOverride ? { draftSearchState, setDraftSearchState } : {})}
            />
          </Stack>
        )}

        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.roomsTitle")}
          </Typography>
          <NumberRangePicker
            options={rooms}
            initialMin={draftSearchState.minRooms}
            initialMax={draftSearchState.maxRooms}
            onRangeChange={(min, max) => {
              setDraftSearchState({ minRooms: min as Room, maxRooms: max as Room });
            }}
            customOptionLabel={(room) => (room === 6 ? "6+" : room)}
          />
        </Stack>

        {/* Built area sits right under rooms: they are the two ways of saying "how big", and a
            realtor filtering on one is usually about to reach for the other. Every host of this
            drawer sends the bounds (the board, the listings-match page, rental-to-sale, the
            contact card's matches and the listing form's source search). */}
        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.squareMeterTitle", {
              defaultValue: "שטח בנוי (מ״ר)"
            })}
          </Typography>
          <SizeRangePicker
            initialMin={draftSearchState.minSquareMeter}
            initialMax={draftSearchState.maxSquareMeter}
            onRangeChange={(min, max) => {
              setDraftSearchState({ minSquareMeter: min, maxSquareMeter: max });
            }}
            hint={translate("listingsMatch.sizeRangePicker.hint", {
              defaultValue: "נכסים שלא צוין בהם שטח לא יוצגו בסינון לפי שטח"
            })}
          />
        </Stack>

        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.parkingSpotsTitle")}
          </Typography>
          <NumberRangePicker
            options={parkingSpots}
            initialMin={draftSearchState.minParkingSpots}
            initialMax={draftSearchState.maxParkingSpots}
            onRangeChange={(min, max) => {
              setDraftSearchState({
                minParkingSpots: min as ParkingSpot,
                maxParkingSpots: max as ParkingSpot
              });
            }}
          />
        </Stack>

        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.floorsTitle")}
          </Typography>
          <FloorRangePicker
            initialMin={draftSearchState.minFloors}
            initialMax={draftSearchState.maxFloors}
            onRangeChange={(min, max) => {
              setDraftSearchState({ minFloors: min as Floor, maxFloors: max as Floor });
            }}
          />
        </Stack>

        <Divider />

        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.priceTitle")}
          </Typography>
          <PriceRangePicker
            initialMinPrice={draftSearchState.minPrice}
            initialMaxPrice={draftSearchState.maxPrice}
            rangeMax={frozenFilters?.priceRangeMax}
            onRangeChange={(min, max) => {
              setDraftSearchState({ minPrice: min, maxPrice: max });
            }}
            includeListingsWithoutPrice={draftSearchState.includeListingsWithoutPrice}
            onIncludeListingsWithoutPriceChange={(include) =>
              setDraftSearchState({ includeListingsWithoutPrice: include })
            }
          />
        </Stack>

        {!frozenFilters?.listingListedFor && (
          <Stack gap={1} width={"100%"}>
            <Typography variant={"subtitle2"}>
              {translate("listingsMatch.filters.listingListedForTitle")}
            </Typography>
            <ToggleButtonGroup
              value={draftSearchState.listingListedFor}
              exclusive
              onChange={(_event, newValue: ListingListedFor) => {
                if (newValue !== null) {
                  setDraftSearchState({ listingListedFor: newValue });
                }
              }}
              size="small"
              fullWidth
            >
              {listingListedForValues.map((value) => (
                <ToggleButton key={value} value={value}>
                  <Typography variant="caption">
                    {translate(`listingsMatch.listingListedFor.${value}`)}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}

        {!frozenFilters?.adTypes && (
          <Stack gap={1} width={"100%"}>
            <Typography variant={"subtitle2"}>
              {translate("listingsMatch.filters.adTypesTitle")}
            </Typography>
            <SimpleMultiSelect
              label={translate("listingsMatch.filters.adTypesTitle")}
              emptyLabel={translate("listingsMatch.filters.allOptions")}
              value={draftSearchState.adTypes}
              options={adTypesValues as unknown as string[]}
              onChange={(value) => setDraftSearchState({ adTypes: value })}
              getOptionLabel={(option) => translate(`listingsMatch.adTypes.${option}`)}
            />
          </Stack>
        )}

        <ListingBooleanPropertiesDetailsBag
          contact
          data={draftSearchState.booleanPropertiesDetails}
          loading={false}
          onChangePropertyDetails={handleChangePropertyDetails}
          hideProperties={hiddenBooleanPropertiesDetailsKeys}
        />

        <Stack sx={{ width: "100%" }} gap={1}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.hidePotentiallyRemovedListings")}
          </Typography>
          <BooleanRadioPicker
            value={draftSearchState.hidePotentiallyRemovedListings}
            onChange={(value) => setDraftSearchState({ hidePotentiallyRemovedListings: value })}
          />
        </Stack>
        {!frozenFilters?.isAssetExclusive && <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.isAssetExclusiveTitle")}
          </Typography>
          <BooleanRadioPicker
            value={draftSearchState.isAssetExclusive}
            onChange={(value) => setDraftSearchState({ isAssetExclusive: value })}
          />
        </Stack>}
        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.onlyPriceChangedTitle")}
          </Typography>
          <BooleanRadioPicker
            value={draftSearchState.onlyPriceChanged}
            onChange={(value) => setDraftSearchState({ onlyPriceChanged: value })}
          />
        </Stack>

        <Stack gap={1} width={"100%"}>
          <Typography variant="subtitle2">
            {translate("listingsMatch.filters.publishedDateTitle")}
          </Typography>
          <DateRangeFilter
            value={draftSearchState.publishedDateRange ?? EMPTY_DATE_RANGE_FILTER}
            onChange={(next) => setDraftSearchState({ publishedDateRange: next })}
            dateLabel={translate("listingsMatch.filters.publishedDateTitle")}
            presets={["all", "last30", "last90", "lastYear", "custom"] as DatePreset[]}
            selectMinWidth="100%"
          />
        </Stack>

        {!frozenFilters?.listingOrigins && (
          <Stack gap={1} width={"100%"} direction="column">
            <Typography variant={"subtitle2"}>
              {translate("listingsMatch.filters.listingOriginsTitle")}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftSearchState.listingOrigins.includes("webot")}
                    disabled={true}
                    onChange={() => {}} // No-op since disabled
                  />
                }
                label={translate("listingsMatch.filters.webotSearching")}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftSearchState.listingOrigins.includes("yad2")}
                    onChange={(e) => {
                      const newOrigins: ListingOrigin[] = e.target.checked
                        ? [...draftSearchState.listingOrigins, "yad2"]
                        : draftSearchState.listingOrigins.filter((origin) => origin !== "yad2");
                      setDraftSearchState({ listingOrigins: newOrigins });
                    }}
                  />
                }
                label={translate("listingsMatch.filters.publicSearching")}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftSearchState.listingOrigins.includes("madlan")}
                    onChange={(e) => {
                      const newOrigins: ListingOrigin[] = e.target.checked
                        ? [...draftSearchState.listingOrigins, "madlan"]
                        : draftSearchState.listingOrigins.filter((origin) => origin !== "madlan");
                      setDraftSearchState({ listingOrigins: newOrigins });
                    }}
                  />
                }
                label={translate("listingsMatch.filters.madlanSearching")}
              />
            </Stack>
          </Stack>
        )}

        <Divider />
        <Button
          variant="contained"
          onClick={onSearch}
          sx={{
            width: { xs: "100%", sm: "auto" }
          }}
        >
          {translate("listingsMatch.searchLabel")}
        </Button>
      </Stack>
    </Drawer>
  );
}
